import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { getProfile, updateProfile } from '../../actions/profileActions';
import { UserRound, UserPen, ArrowLeft, Mail, MapPin, Calendar, Clock } from 'lucide-react';
import Spinner from '../common/Spinner';
import ProfileSidebar from './ProfileSidebar';
import ProfilePictureUpload from './ProfilePictureUpload';
import SecurityTab from './SecurityTab';
import StudentStats from './StudentStats';
import ActivityHistoryTab from './ActivityHistoryTab';
import BadgesTab from './BadgesTab';

const Profile = ({
  auth: { user, lastAction },
  profile: { profile, loading, updateSuccess, updateError },
  getProfile,
  updateProfile
}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const userId = searchParams.get('id');

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    lastName: '',
    country: '',
    birthYear: ''
  });
  const [activeTab, setActiveTab] = useState('my-profile');

  // Determine if viewing own profile or someone else's
  const isOwnProfile = !userId || (user && userId === user._id);
  const canEdit = isOwnProfile;
  const isViewingOther = !isOwnProfile && (user?.role === 'admin' || user?.role === 'platform_admin' || user?.role === 'instructor');

  const countries = [
    'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Argentina', 'Armenia', 'Australia',
    'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium',
    'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei',
    'Bulgaria', 'Burkina Faso', 'Burundi', 'Cambodia', 'Cameroon', 'Canada', 'Cape Verde',
    'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo',
    'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark', 'Djibouti', 'Dominica',
    'Dominican Republic', 'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia',
    'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana',
    'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana', 'Haiti', 'Honduras',
    'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy',
    'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'Kuwait', 'Kyrgyzstan',
    'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania',
    'Luxembourg', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands',
    'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro',
    'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand',
    'Nicaragua', 'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway', 'Oman', 'Pakistan',
    'Palau', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal',
    'Qatar', 'Romania', 'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia',
    'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Saudi Arabia', 'Senegal', 'Serbia',
    'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia',
    'South Africa', 'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname',
    'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste',
    'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu', 'Uganda',
    'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan',
    'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
  ];

  useEffect(() => {
    getProfile(userId);
  }, [getProfile, userId]);

  useEffect(() => {
    if (profile) {
      setFormData({
        name: profile.name || '',
        lastName: profile.lastName || '',
        country: profile.country || '',
        birthYear: profile.birthYear || profile.birthDate ? (profile.birthYear || new Date(profile.birthDate).getFullYear()) : ''
      });
    }
  }, [profile]);

  if (loading || !profile) return <Spinner />;

  const onChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const onSubmit = e => {
    e.preventDefault();
    updateProfile(formData);
    setIsEditing(false);
  };

  const handleProfilePictureUpload = async (profilePictureData) => {
    try {
      await updateProfile({ profilePicture: profilePictureData });
    } catch (error) {
      throw error;
    }
  };

  const handleProfilePictureDelete = async () => {
    try {
      await updateProfile({ profilePicture: null });
    } catch (error) {
      throw error;
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'platform_admin':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'admin':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'instructor':
        return 'bg-amber-50 text-amber-800 border-amber-200';
      case 'student':
      default:
        return 'bg-blue-50 text-blue-800 border-blue-200';
    }
  };

  const formatRoleName = (role) => {
    if (role === 'platform_admin') return 'Platform Admin';
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  const renderProfileHeader = () => (
    <div className="relative mb-8">
      {/* Gradient Banner */}
      <div className="h-36 sm:h-44 rounded-xl bg-gradient-to-br from-[#054581] via-[#1086a4] to-[#0ea5c7] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-4 right-8 w-32 h-32 rounded-full bg-white/20" />
          <div className="absolute bottom-2 left-12 w-20 h-20 rounded-full bg-white/15" />
          <div className="absolute top-8 left-1/3 w-16 h-16 rounded-full bg-white/10" />
        </div>
        {isViewingOther && (
          <button
            onClick={() => navigate(-1)}
            className="absolute top-4 left-4 flex items-center gap-2 text-white/90 hover:text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-lg transition-all"
          >
            <ArrowLeft size={18} />
            <span className="text-sm font-medium">Back</span>
          </button>
        )}
      </div>

      {/* Profile Card overlapping banner */}
      <div className="mx-4 sm:mx-6 -mt-16 relative">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row items-center sm:items-end gap-4 sm:gap-6">
            {/* Avatar */}
            <div className="flex-shrink-0 -mt-16 sm:-mt-20 relative">
              <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full overflow-hidden border-4 border-white shadow-lg bg-white">
                {profile?.profilePicture?.url || profile?.profilePicture?.data ? (
                  <img
                    src={profile.profilePicture.url || `data:${profile.profilePicture.contentType};base64,${profile.profilePicture.data}`}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#1086a4]/10 to-[#054581]/10 flex items-center justify-center">
                    <UserRound size={48} className="text-[#1086a4]" />
                  </div>
                )}
              </div>
              {canEdit && (
                <div className="absolute -bottom-1 -right-1">
                  <ProfilePictureUpload
                    currentPicture={profile?.profilePicture}
                    onUpload={handleProfilePictureUpload}
                    onDelete={handleProfilePictureDelete}
                  />
                </div>
              )}
            </div>

            {/* Name & Info */}
            <div className="flex-1 text-center sm:text-left pb-1">
              <h1 className="text-2xl font-bold text-[#054581]">
                {profile.name} {profile.lastName}
              </h1>
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mt-2">
                <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full border ${getRoleBadgeClass(profile.role)}`}
                  style={{ textTransform: 'capitalize' }}>
                  {formatRoleName(profile.role)}
                </span>
                {profile.country && (
                  <span className="inline-flex items-center gap-1 text-sm text-gray-500">
                    <MapPin size={14} />
                    {profile.country}
                  </span>
                )}
                <span className="inline-flex items-center gap-1 text-sm text-gray-500">
                  <Clock size={14} />
                  Joined {new Date(profile.createdAt).toLocaleDateString([], { month: 'short', year: 'numeric' })}
                </span>
              </div>
              {isViewingOther && (
                <div className="mt-2 text-sm text-gray-500 italic">
                  Viewing as {user?.role}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAboutMe = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-bold text-[#054581]">About Me</h2>
        {canEdit && (
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors border
              bg-white text-[#1086a4] border-[#1086a4] hover:bg-[#1086a4] hover:text-white"
          >
            <UserPen size={16} />
            {isEditing ? 'Cancel' : 'Edit'}
          </button>
        )}
      </div>

      {updateSuccess && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
          Profile updated successfully!
        </div>
      )}
      {updateError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          {updateError.msg}
        </div>
      )}

      <form onSubmit={onSubmit}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">First Name</label>
            {isEditing ? (
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={onChange}
                className="block w-full rounded-lg border border-gray-300 bg-white text-[#1e2d40] px-3 py-2.5 shadow-sm focus:border-[#1086a4] focus:ring-1 focus:ring-[#1086a4] transition-colors"
              />
            ) : (
              <div className="text-[#1e2d40] font-medium py-2.5">{profile.name}</div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Last Name</label>
            {isEditing ? (
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={onChange}
                className="block w-full rounded-lg border border-gray-300 bg-white text-[#1e2d40] px-3 py-2.5 shadow-sm focus:border-[#1086a4] focus:ring-1 focus:ring-[#1086a4] transition-colors"
              />
            ) : (
              <div className="text-[#1e2d40] font-medium py-2.5">{profile.lastName}</div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Email</label>
            <div className="flex items-center gap-2 text-[#1e2d40] font-medium py-2.5">
              <Mail size={16} className="text-gray-400" />
              {profile.email}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Country</label>
            {isEditing ? (
              <select
                name="country"
                value={formData.country}
                onChange={onChange}
                className="block w-full rounded-lg border border-gray-300 bg-white text-[#1e2d40] px-3 py-2.5 shadow-sm focus:border-[#1086a4] focus:ring-1 focus:ring-[#1086a4] transition-colors"
              >
                <option value="">Select your country</option>
                {countries.map((country) => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            ) : (
              <div className="flex items-center gap-2 text-[#1e2d40] font-medium py-2.5">
                <MapPin size={16} className="text-gray-400" />
                {profile.country || 'Not set'}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Birth Year</label>
            {isEditing ? (
              <select
                name="birthYear"
                value={formData.birthYear}
                onChange={onChange}
                className="block w-full rounded-lg border border-gray-300 bg-white text-[#1e2d40] px-3 py-2.5 shadow-sm focus:border-[#1086a4] focus:ring-1 focus:ring-[#1086a4] transition-colors"
              >
                <option value="">Select year</option>
                {Array.from({ length: 121 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            ) : (
              <div className="flex items-center gap-2 text-[#1e2d40] font-medium py-2.5">
                <Calendar size={16} className="text-gray-400" />
                {profile.birthYear || (profile.birthDate ? new Date(profile.birthDate).getFullYear() : 'Not set')}
              </div>
            )}
          </div>
        </div>
        {isEditing && (
          <div className="flex justify-end mt-6">
            <button
              type="submit"
              className="bg-[#1086a4] text-white px-6 py-2.5 rounded-lg font-semibold shadow-sm hover:bg-[#054581] transition-colors"
            >
              Save Changes
            </button>
          </div>
        )}
      </form>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-4 px-2 sm:py-6 sm:px-4">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col md:flex-row overflow-hidden">
          <ProfileSidebar
            activeTab={activeTab}
            onTabChange={setActiveTab}
            showSecurityTab={canEdit}
          />
          <div className="flex-1 border-t md:border-t-0 md:border-l border-gray-200">
            <div className="p-4 sm:p-6 lg:p-8">
              {activeTab === 'my-profile' ? (
                <>
                  {renderProfileHeader()}
                  {renderAboutMe()}
                  {isViewingOther && profile?.role === 'student' && (
                    <div className="mt-6">
                      <StudentStats userId={userId} />
                    </div>
                  )}
                  <div className="mt-6">
                    <BadgesTab userId={userId || user?.id || user?._id} />
                  </div>
                  <div className="mt-6">
                    <ActivityHistoryTab />
                  </div>
                </>
              ) : activeTab === 'security' && canEdit ? (
                <SecurityTab />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

Profile.propTypes = {
  auth: PropTypes.object.isRequired,
  profile: PropTypes.object.isRequired,
  getProfile: PropTypes.func.isRequired,
  updateProfile: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  auth: state.auth,
  profile: state.profile
});

export default connect(mapStateToProps, { getProfile, updateProfile })(Profile);
